/**
 * Стенды Гермеса в настройках платформы: список «адрес + ключ», общий на всю
 * систему. Пространства выбирают стенд из этого списка, а не вбивают адрес и
 * ключ у себя. Правит только админ экземпляра.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';
import type { HermesStand } from '@lens/shared';
import {
  listHermesStands,
  createHermesStand,
  updateHermesStand,
  deleteHermesStand,
  testHermesStand,
} from './api.ts';
import { spacing, radius, shadows } from '../../shared/theme/tokens.ts';

const { Text } = Typography;

interface EditState {
  /** null — заводим новый стенд. */
  stand: HermesStand | null;
  name: string;
  baseUrl: string;
  apiKey: string;
  isDefault: boolean;
}

const EMPTY_EDIT: EditState = { stand: null, name: '', baseUrl: '', apiKey: '', isDefault: false };

export function HermesStandsCard(): JSX.Element {
  const [stands, setStands] = useState<HermesStand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStands(await listHermesStands());
      setError(null);
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось загрузить стенды');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSave(): Promise<void> {
    if (!edit) return;
    const name = edit.name.trim();
    if (!name) {
      void message.error('Укажите название стенда');
      return;
    }
    if (!edit.stand && !edit.apiKey.trim()) {
      void message.error('Для нового стенда нужен ключ (X-API-Key)');
      return;
    }
    setSaving(true);
    try {
      const baseUrl = edit.baseUrl.trim() ? edit.baseUrl.trim() : null;
      if (edit.stand) {
        await updateHermesStand(edit.stand.id, {
          name,
          baseUrl,
          isDefault: edit.isDefault,
          ...(edit.apiKey.trim() ? { apiKey: edit.apiKey.trim() } : {}),
        });
      } else {
        await createHermesStand({
          name,
          baseUrl,
          apiKey: edit.apiKey.trim(),
          isDefault: edit.isDefault,
        });
      }
      setEdit(null);
      await load();
      void message.success('Стенд сохранён');
    } catch (err: any) {
      void message.error(err?.message ?? 'Не удалось сохранить стенд');
    } finally {
      setSaving(false);
    }
  }

  async function handleTest(stand: HermesStand): Promise<void> {
    setTestingId(stand.id);
    try {
      const res = await testHermesStand(stand.id);
      if (res.ok) void message.success(`«${stand.name}» отвечает (статус задачи: ${res.taskStatus})`);
      else void message.error(`«${stand.name}»: ${res.message}`);
    } catch (err: any) {
      void message.error(err?.message ?? 'Проверка не удалась');
    } finally {
      setTestingId(null);
    }
  }

  async function handleDelete(stand: HermesStand): Promise<void> {
    try {
      await deleteHermesStand(stand.id);
      await load();
      void message.success('Стенд удалён');
    } catch (err: any) {
      void message.error(err?.message ?? 'Не удалось удалить стенд');
    }
  }

  return (
    <Card
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.sm }}>
          <ExperimentOutlined />
          Стенды Гермеса
        </span>
      }
      extra={
        <Button type="primary" onClick={() => setEdit({ ...EMPTY_EDIT })} data-testid="hermes-stand-add">
          Добавить стенд
        </Button>
      }
      loading={loading}
      style={{
        width: '100%',
        marginBottom: spacing.base,
        borderRadius: radius.lg,
        boxShadow: shadows.card,
      }}
    >
      {error && <Alert type="error" message={error} style={{ marginBottom: spacing.base }} />}

      <Text style={{ display: 'block', marginBottom: spacing.base, fontSize: 12, color: 'var(--color-text-secondary)' }}>
        Адрес и ключ задаются здесь один раз. В настройках пространства админ только выбирает
        стенд из списка — и переключает пространство на другой стенд одним действием.
      </Text>

      <Table<HermesStand>
        rowKey="id"
        dataSource={stands}
        pagination={false}
        size="small"
        data-testid="hermes-stands-table"
        locale={{ emptyText: 'Пока ни одного стенда' }}
        columns={[
          {
            title: 'Название',
            dataIndex: 'name',
            render: (name: string, stand) => (
              <Space size={spacing.xs}>
                <span>{name}</span>
                {stand.isDefault && <Tag color="blue">по умолчанию</Tag>}
              </Space>
            ),
          },
          {
            title: 'Адрес',
            dataIndex: 'baseUrl',
            render: (_: unknown, stand) =>
              stand.baseUrlRaw ? (
                <Text code style={{ fontSize: 12 }}>{stand.baseUrlRaw}</Text>
              ) : (
                <Tooltip title="Адрес не задан — берётся HERMES_BASE_URL">
                  <Text type="secondary" style={{ fontSize: 12 }}>{stand.baseUrl ?? 'не задан'}</Text>
                </Tooltip>
              ),
          },
          {
            title: 'Используется',
            dataIndex: 'usedByConfigs',
            width: 120,
            render: (count: number) =>
              count > 0 ? `${count} настр.` : <Text type="secondary">—</Text>,
          },
          {
            title: '',
            key: 'actions',
            width: 220,
            render: (_: unknown, stand) => (
              <Space size={spacing.xs}>
                <Button size="small" loading={testingId === stand.id} onClick={() => void handleTest(stand)}>
                  Проверить
                </Button>
                <Button
                  size="small"
                  onClick={() =>
                    setEdit({
                      stand,
                      name: stand.name,
                      baseUrl: stand.baseUrlRaw ?? '',
                      apiKey: '',
                      isDefault: stand.isDefault,
                    })
                  }
                >
                  Изменить
                </Button>
                <Popconfirm
                  title="Удалить стенд?"
                  okText="Удалить"
                  cancelText="Отмена"
                  onConfirm={() => void handleDelete(stand)}
                  disabled={stand.usedByConfigs > 0}
                >
                  <Tooltip
                    title={
                      stand.usedByConfigs > 0
                        ? 'Сначала переключите пространства на другой стенд'
                        : undefined
                    }
                  >
                    <Button size="small" danger disabled={stand.usedByConfigs > 0}>
                      Удалить
                    </Button>
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        open={edit !== null}
        title={edit?.stand ? `Стенд «${edit.stand.name}»` : 'Новый стенд Гермеса'}
        onCancel={() => setEdit(null)}
        onOk={() => void handleSave()}
        confirmLoading={saving}
        okText="Сохранить"
        cancelText="Отмена"
        destroyOnClose
      >
        {edit && (
          <Space direction="vertical" style={{ width: '100%' }} size={spacing.base}>
            <div>
              <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Название</Text>
              <Input
                value={edit.name}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                placeholder="Прод / Тест"
                data-testid="hermes-stand-name"
              />
            </div>
            <div>
              <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Base URL</Text>
              <Input
                value={edit.baseUrl}
                onChange={(e) => setEdit({ ...edit, baseUrl: e.target.value })}
                placeholder="https://hermes.example.ru"
                autoComplete="off"
                data-testid="hermes-stand-url"
              />
              <Text style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                Пусто — берётся HERMES_BASE_URL.
              </Text>
            </div>
            <div>
              <Text style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>X-API-Key</Text>
              <Input.Password
                value={edit.apiKey}
                onChange={(e) => setEdit({ ...edit, apiKey: e.target.value })}
                placeholder={edit.stand ? '•••• (задан, оставьте пустым чтобы не менять)' : 'hsk_...'}
                autoComplete="new-password"
                data-testid="hermes-stand-key"
              />
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.xs }}>
              <input
                type="checkbox"
                checked={edit.isDefault}
                onChange={(e) => setEdit({ ...edit, isDefault: e.target.checked })}
                data-testid="hermes-stand-default"
              />
              <Text>Предлагать по умолчанию в новых пространствах</Text>
            </label>
          </Space>
        )}
      </Modal>
    </Card>
  );
}
